import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import VideoMuteIcon from '~/icons/components/VideoMuteIcon';
import VideoPauseIcon from '~/icons/components/VideoPauseIcon';
import VideoPlayIcon from '~/icons/components/VideoPlayIcon';
import VideoVolumeIcon from '~/icons/components/VideoVolumeIcon';
import PressableIconButton from '~/shared/components/PressableIconButton';
import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';

import { VIDEO_AUTOPLAY_THRESHOLD } from '../../constants';
import useFadeOutUnmount from '../../hooks/useFadeOutUnmount';
import { getCoverSize, getVimeoEmbedUrl } from '../../utils/video';

import AssetCardFallback from './AssetCardFallback';
import AssetCardSkeleton from './AssetCardSkeleton';

const ControlsOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  opacity: 1;
  pointer-events: auto;
  transition: opacity 150ms ease;

  @media (hover: hover) {
    opacity: 0;
    pointer-events: none;
  }
`;

const VideoContainer = styled.div<{
  $aspectRatio: number;
  $isFullRow: boolean;
  $isUncropped: boolean;
}>`
  position: relative;
  width: 100%;
  /* Uncropped mode: always enforce aspect-ratio so the container takes its natural
     intrinsic height regardless of sibling product cards — mirrors the <img height: auto>
     path in AssetCardImage. Otherwise: stretch to row height (height: 100%) and only fall
     back to aspect-ratio when the video owns the full row (no product cards to supply
     track height). */
  ${({ $isFullRow, $isUncropped, $aspectRatio }) =>
    $isUncropped || $isFullRow
      ? `aspect-ratio: ${$aspectRatio};`
      : 'height: 100%;'}
  overflow: hidden;

  @media (hover: hover) {
    &:hover ${ControlsOverlay} {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const VideoIframe = styled.iframe`
  position: absolute;
  border: 0;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
`;

const PlayPauseButton = styled(PressableIconButton)`
  position: absolute;
  z-index: 2;
  pointer-events: auto;
  left: 6px;
  bottom: 6px;

  @media (hover: hover) {
    left: 12px;
    bottom: 12px;
  }
`;

const MuteButton = styled(PressableIconButton)`
  position: absolute;
  z-index: 2;
  pointer-events: auto;
  right: 6px;
  bottom: 6px;

  @media (hover: hover) {
    right: 12px;
    bottom: 12px;
  }
`;

const LoadingOverlay = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  transition: opacity 300ms ease;
  opacity: ${({ $isVisible }) => ($isVisible ? 1 : 0)};
  pointer-events: none;
`;

// Client-side counter to generate unique player IDs per AssetCardVideo instance.
// Required so each iframe's Vimeo postMessage events can be attributed to the
// correct player when multiple videos coexist on the same page.
// Kept in a ref to avoid SSR hydration mismatches from a module-level counter
// that would persist and increment across server requests.
let nextPlayerId = 0;

const usePlayerId = (): string | null => {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null && typeof window !== 'undefined') {
    nextPlayerId += 1;
    idRef.current = `gridv2-asset-video-${nextPlayerId}`;
  }
  return idRef.current;
};

interface AssetCardVideoProps {
  aspectRatio: number;
  url: string;
  isFullRow?: boolean;
}

const AssetCardVideo = ({
  aspectRatio,
  url,
  isFullRow = false,
}: AssetCardVideoProps) => {
  const playerId = usePlayerId();
  const embedUrl = getVimeoEmbedUrl(url, playerId ?? undefined);
  const { enabled: isAssetNoCropEnabled } = useHasFeature(
    featureNames.GRID_V2_ASSET_NO_CROP
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<HTMLIFrameElement>(null);
  const [playerSize, setPlayerSize] = useState<{
    width: number | string;
    height: number | string;
  }>({
    width: '100%',
    height: '100%',
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isPausedByUser, setIsPausedByUser] = useState(false);
  const {
    isMounted: showLoadingOverlay,
    onTransitionEnd: onLoadingOverlayTransitionEnd,
  } = useFadeOutUnmount(!isReady);

  // Force immediate teardown of the Vimeo media pipeline when the card scrolls
  // off-screen and unmounts. Without this, iOS Safari can retain the iframe's
  // decoded video buffers and audio session for several seconds after DOM
  // removal — a major contributor to memory pressure during long scrolls.
  // The iframe reference is captured at mount time per the
  // react-hooks/exhaustive-deps guidance; this is safe because the iframe DOM
  // node is structurally stable (rendered by a single JSX element), so the
  // captured reference points to the same node throughout the component's
  // lifetime.
  useEffect(() => {
    const iframe = playerRef.current;
    return () => {
      if (iframe) {
        iframe.src = 'about:blank';
      }
    };
  }, []);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return;
    }

    const updateSize = () => {
      const { width, height } = node.getBoundingClientRect();
      setPlayerSize(
        getCoverSize({
          containerWidth: width,
          containerHeight: height,
          aspectRatio,
        })
      );
    };

    updateSize();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateSize());
      observer.observe(node);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [aspectRatio]);

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setIsInView(Boolean(entry?.isIntersecting));
      },
      { threshold: VIDEO_AUTOPLAY_THRESHOLD }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes('vimeo.com')) {
        return;
      }

      if (!event.data) {
        return;
      }

      let payload = event.data;
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload);
        } catch (error) {
          return;
        }
      }

      if (payload?.player_id !== playerId) {
        return;
      }

      if (payload?.event === 'ready') {
        setIsReady(true);
        return;
      }

      if (payload?.event === 'play') {
        setIsPlaying(true);
      }

      if (payload?.event === 'pause' || payload?.event === 'ended') {
        setIsPlaying(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [playerId]);

  const postPlayerMessage = useCallback(
    (method: string, value?: string | number) => {
      const target = playerRef.current?.contentWindow;
      if (!target) {
        return;
      }

      target.postMessage(
        JSON.stringify({
          method,
          value,
          player_id: playerId,
        }),
        '*'
      );
    },
    [playerId]
  );

  const handleTogglePlay = useCallback(() => {
    if (!isReady) {
      return;
    }

    if (isPlaying) {
      setIsPausedByUser(true);
      postPlayerMessage('pause');
      return;
    }

    setIsPausedByUser(false);
    postPlayerMessage('play');
  }, [isPlaying, isReady, postPlayerMessage]);

  const handleToggleMute = useCallback(() => {
    if (!isReady) {
      return;
    }

    const nextMuted = !isMuted;
    setIsMuted(nextMuted);
    postPlayerMessage('setVolume', nextMuted ? 0 : 1);
  }, [isMuted, isReady, postPlayerMessage]);

  useLayoutEffect(() => {
    if (!isReady) {
      return;
    }

    postPlayerMessage('addEventListener', 'play');
    postPlayerMessage('addEventListener', 'pause');
    postPlayerMessage('addEventListener', 'ended');
    postPlayerMessage('setVolume', 0);
  }, [isReady, postPlayerMessage]);

  useLayoutEffect(() => {
    if (!isReady) {
      return;
    }

    if (isInView) {
      if (isPausedByUser) {
        return;
      }
      postPlayerMessage('play');
      return;
    }

    postPlayerMessage('pause');
  }, [isInView, isPausedByUser, isReady, postPlayerMessage]);

  if (!embedUrl || hasError) {
    return <AssetCardFallback />;
  }

  return (
    <VideoContainer
      ref={containerRef}
      $aspectRatio={aspectRatio}
      $isFullRow={isFullRow}
      $isUncropped={isAssetNoCropEnabled}
    >
      {showLoadingOverlay && (
        <LoadingOverlay
          $isVisible={!isReady}
          onTransitionEnd={onLoadingOverlayTransitionEnd}
        >
          <AssetCardSkeleton />
        </LoadingOverlay>
      )}
      <VideoIframe
        ref={playerRef}
        src={embedUrl}
        title="Asset video"
        allow="autoplay; fullscreen; picture-in-picture"
        allowFullScreen
        onError={() => setHasError(true)}
        style={{
          width: playerSize.width,
          height: playerSize.height,
        }}
      />
      <ControlsOverlay>
        <PlayPauseButton onClick={handleTogglePlay}>
          {isPlaying ? <VideoPauseIcon /> : <VideoPlayIcon />}
        </PlayPauseButton>
        <MuteButton onClick={handleToggleMute}>
          {isMuted ? <VideoMuteIcon /> : <VideoVolumeIcon />}
        </MuteButton>
      </ControlsOverlay>
    </VideoContainer>
  );
};

export default AssetCardVideo;
